import {ApiClient, type ErrorResponse} from '@hconnect/apiclient'
import {useUpmPlantContext, type UpmPlant} from '@hconnect/upm'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, OPTIMIZER_SETTINGS_QUERY_KEY, getOptimizerSettingsPath} from '../consts'
import {mapOptimizerSettingsToDTO} from '../selectors'
import {OptimizerSettings} from '../types'
import {notifyIfErrorMessage} from '../utils'

export const useEditOptimizerSettings = () => {
  const {plantId: plantCode}: UpmPlant = useUpmPlantContext()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()

  const {mutate, isPending: isLoading} = useMutation<
    void,
    AxiosError<ErrorResponse>,
    OptimizerSettings
  >({
    mutationFn: async (settings) => {
      await ApiClient.axiosInstance.patch<void>(
        getOptimizerSettingsPath(plantCode),
        mapOptimizerSettingsToDTO(settings)
      )
    },
    onError: (error) => {
      spawnErrorToast(t('error.apology'))
      notifyIfErrorMessage(error.response?.data.detail, raiseError, spawnErrorToast)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [OPTIMIZER_SETTINGS_QUERY_KEY, {plantCode}],
        refetchType: 'all'
      })
    },
    onSuccess: () => {
      spawnSuccessToast(t('success.editOptimizerSettings'))
    }
  })

  const editOptimizerSettings = useCallback(
    (settings: OptimizerSettings) => mutate(settings),
    [mutate]
  )

  return {editOptimizerSettings, isLoading}
}
